import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  EMPTY, Observable, Subject, catchError, concatMap, debounce, defer, finalize, map, merge, tap, throwError, timer,
} from 'rxjs';
import { SigningService } from '../../signing/signing.service';
import { ContactFormValue, validateContact } from '../contact/contact-form.store';
import { CustomerIntakeResponse, IntakeSigning, SubmitCustomerIntakeResponse, toE164 } from '../intake/customer-intake.service';
import { OwnerRelationship, SignupAddressStore } from '../address/signup-address.store';
import { ContactSubmission, SignupSubmitService } from '../intake/signup-submit.service';

/**
 * - `idle`: the required fields aren't all filled in yet; nothing has been saved.
 * - `preparing`: the intake is saving and the backend is generating (or regenerating) Form 50-162.
 * - `signing`: the SDK is open in the pad.
 * - `closed`: the signer closed the SDK; the same request can be reopened.
 * - `signed`: Form 50-162 is signed; the details can't change, and the form can be submitted.
 * - `login-required`: the email already has an account, so no form was generated here.
 * - `error`: the form can't be signed here as things stand.
 *
 * Every state before `signed` takes a save: a change (or Sign form again) regenerates the form from the corrected
 * details.
 */
export type SignupEnrollmentState = 'idle' | 'preparing' | 'signing' | 'closed' | 'signed' | 'login-required' | 'error';

export type SignupEnrollmentEvent =
  /** Sign form was pressed, or the details changed after it was, with every required field present and valid. */
  | 'FIELDS_COMPLETE'
  /** The intake returned a signing form. */
  | 'OPENED'
  | 'LOGIN_REQUIRED'
  /** Saved, but there's nothing to sign here (signing couldn't start, or the intake can't change). */
  | 'UNAVAILABLE'
  /** The save was rejected and nothing changed, so it goes back to the state it left. */
  | 'SAVE_FAILED'
  | 'SIGNED'
  | 'CANCELLED'
  | 'DECLINED'
  | 'SDK_ERROR'
  | 'REOPEN';

/** `resume`: the state `preparing` started from. */
type Target = SignupEnrollmentState | 'resume';

/** Transitions not listed are ignored: e.g. nothing saves once `signed`, and a closed SDK only reopens. */
const TRANSITIONS: Record<SignupEnrollmentState, Partial<Record<SignupEnrollmentEvent, Target>>> = {
  idle: { FIELDS_COMPLETE: 'preparing' },
  preparing: { OPENED: 'signing', LOGIN_REQUIRED: 'login-required', UNAVAILABLE: 'error', SAVE_FAILED: 'resume' },
  signing: { FIELDS_COMPLETE: 'preparing', SIGNED: 'signed', CANCELLED: 'closed', DECLINED: 'error', SDK_ERROR: 'error' },
  closed: { FIELDS_COMPLETE: 'preparing', REOPEN: 'signing' },
  'login-required': { FIELDS_COMPLETE: 'preparing' },
  error: { FIELDS_COMPLETE: 'preparing' },
  signed: {},
};

export function nextEnrollmentState(
  state: SignupEnrollmentState, event: SignupEnrollmentEvent, resume: SignupEnrollmentState = 'idle',
): SignupEnrollmentState | null {
  const target = TRANSITIONS[state][event];
  return target === 'resume' ? resume : target ?? null;
}

/** The enroll form requires every contact field and the TCPA consent; marketing consent is optional. */
export function hasRequiredEnrollFields(contact: ContactFormValue): boolean {
  return Object.keys(validateContact(contact, { phoneRequired: true })).length === 0;
}

/** What a save sends, normalized: an update that wouldn't change any of it is skipped. */
function saveKey(s: ContactSubmission, ownerRelationship: OwnerRelationship): string {
  return JSON.stringify([
    s.firstName.trim(), s.lastName.trim(), s.email.trim().toLowerCase(), toE164(s.phone), s.address,
    ownerRelationship,
  ]);
}

/**
 * Quiet time after the last change before updating the form, so several edits (a name typed out, a couple of
 * corrections) send one save: each can regenerate Form 50-162, and the backend throttles saves.
 */
export const UPDATE_DEBOUNCE_MS = 1000;

export const SIGN_REQUIRED_ERROR = 'Sign Form 50-162 to enroll.';
const REJECTED_ERROR = "We couldn't prepare Form 50-162. Check your details and try again.";
// No "log in" here: a new customer can't log in until their account is set up
const SUPPORT_EMAIL = 'propertytax@kin.com';
const UNAVAILABLE_ERROR = `Your details are saved, but Form 50-162 can't be signed here right now. Contact us at ${SUPPORT_EMAIL} to finish enrolling.`;

/**
 * Signature pad state machine for /signup.
 *
 * Generates Form 50-162 the way /sign does, but through `PUT /customer-intake`: the public page has no login
 * session for `POST /signing/request`. The save creates the account, and then the backend starts the same
 * signature request (`getOrCreateSignatureRequest`; it resolves the county from the
 * address, so the intake has no `county` field). The response's `signing` opens in the pad with the same SDK
 * handling as /sign.
 *
 * Sign form starts the save; presses while one is in flight are dropped. After that, until the form is signed,
 * a change to the text inputs or the "Is this you?" answer updates it: changes are debounced, an update that
 * changes nothing is skipped, and the backend regenerates the form from the rest. Saves run one at a time, in
 * order. Enrolling submits the intake once (`submitIntake`).
 *
 * A signature made while a change is still waiting out the debounce is held until that change is saved: if it
 * changes nothing, the signature counts; if it regenerates the form, the signature was on the old one, so the
 * updated form opens to sign instead. Either way the signed form matches the details on the page.
 */
@Injectable({ providedIn: 'root' })
export class SignupEnrollmentService {
  private signing = inject(SigningService);
  private submit = inject(SignupSubmitService);
  private addressStore = inject(SignupAddressStore);

  readonly state = signal<SignupEnrollmentState>('idle');
  /** What `preparing` is waiting on, for the pad's status line. */
  readonly preparingLabel = signal('');
  /** The open request: signingUrl + clientId for the SDK. */
  readonly request = signal<IntakeSigning | null>(null);
  readonly signedAt = signal<Date | null>(null);
  readonly error = signal('');
  readonly isSigned = computed(() => this.state() === 'signed');
  /** Once signed, the details are what the form was signed with. */
  readonly locksContact = computed(() => this.isSigned());

  private readonly signRequests = new Subject<ContactSubmission>();
  private readonly changes = new Subject<ContactSubmission>();
  /** Ends the debounce early: saves the waiting change now. */
  private readonly flushes = new Subject<void>();
  /** The change waiting out the debounce, if any. */
  private pendingChange: ContactSubmission | null = null;
  /** The SDK reported a signature while a change was pending; see `markSigned`. */
  private signatureHeld = false;
  /** The last save the backend accepted (see `saveKey`). */
  private savedKey: string | null = null;

  constructor() {
    merge(
      this.signRequests.pipe(map(submission => ({ submission, update: false }))),
      this.changes.pipe(
        tap(submission => (this.pendingChange = submission)),
        debounce(() => merge(timer(UPDATE_DEBOUNCE_MS), this.flushes)),
        map(submission => {
          this.pendingChange = null;
          return { submission, update: true };
        }),
      ),
    ).pipe(
      // One at a time, so a change made while saving is saved after it, from the newest responses
      concatMap(({ submission, update }) => this.save(submission, update).pipe(finalize(() => this.releaseSignature()))),
      takeUntilDestroyed(inject(DestroyRef)),
    ).subscribe();
  }

  /** The pad's Sign form button, once the enroll form's required fields check out; see `save`. */
  requestSigning(submission: ContactSubmission): void {
    // A double press doesn't generate the form twice
    if (this.state() !== 'preparing') this.signRequests.next(submission);
  }

  /** A text input or the "Is this you?" answer changed: updates the form once things settle; see `save`. */
  fieldsChanged(submission: ContactSubmission): void {
    this.changes.next(submission);
  }

  /** Enroll CTA without a signature: points the signer at the pad. */
  requireSignature(): void {
    if (['idle', 'signing', 'closed'].includes(this.state())) this.error.set(SIGN_REQUIRED_ERROR);
  }

  /** The form's one submit, once Form 50-162 is signed. */
  submitIntake(): Observable<SubmitCustomerIntakeResponse> {
    if (!this.isSigned()) return throwError(() => new Error(SIGN_REQUIRED_ERROR));
    return this.submit.submitIntake();
  }

  reopen(): void {
    if (this.request() && this.dispatch('REOPEN')) this.error.set('');
  }

  // SDK events (wired by SignaturePadComponent, same handling as /sign)

  /** Held while a change is pending, so the change is saved first; see the class notes. */
  markSigned(): void {
    if (this.state() !== 'signing') return;
    if (this.pendingChange) {
      this.signatureHeld = true;
      this.flushes.next();
      return;
    }
    if (!this.dispatch('SIGNED')) return;
    this.signing.markSigningComplete();
    this.signedAt.set(new Date());
    this.error.set('');
  }

  cancel(): void {
    // The SDK closing after a held signature isn't a cancel
    if (this.signatureHeld) return;
    if (this.dispatch('CANCELLED')) this.error.set(SIGN_REQUIRED_ERROR);
  }

  decline(): void {
    if (this.dispatch('DECLINED')) this.error.set(`You declined Form 50-162. To enroll later, contact us at ${SUPPORT_EMAIL}.`);
  }

  fail(message: string): void {
    if (this.dispatch('SDK_ERROR')) this.error.set(message);
  }

  reset(): void {
    this.state.set('idle');
    this.preparingLabel.set('');
    this.request.set(null);
    this.signedAt.set(null);
    this.error.set('');
    this.savedKey = null;
    this.pendingChange = null;
    this.signatureHeld = false;
  }

  /** A save's turn is over: a held signature counts if nothing regenerated the form meanwhile. */
  private releaseSignature(): void {
    if (!this.signatureHeld || this.pendingChange) return;
    this.signatureHeld = false;
    this.markSigned();
  }

  /**
   * Checked when the save's turn comes: nothing is sent unless every required field is present and valid, and the
   * state takes a save (e.g. not once signed). An `update` (a change, not Sign form) is sent only once the form has
   * been generated, and only when it changes something since the last save.
   */
  private save(submission: ContactSubmission, update: boolean): Observable<unknown> {
    return defer(() => {
      const { address } = submission;
      const ownerRelationship = this.addressStore.ownerRelationship();
      if (!address || !this.addressStore.isServiceable() || !hasRequiredEnrollFields(submission)) return EMPTY;
      // Unanswered (Sign form shows the error), or "None of these apply": not the owner, so nothing to sign
      // (the page freezes the form too)
      if (!ownerRelationship || ownerRelationship === 'none') return EMPTY;
      const key = saveKey(submission, ownerRelationship);
      if (update && (this.state() === 'idle' || key === this.savedKey)) return EMPTY;

      const resume = this.state();
      if (!this.dispatch('FIELDS_COMPLETE')) return EMPTY;
      // The form is regenerated: a held signature was on the old one
      this.signatureHeld = false;
      this.error.set('');
      this.preparingLabel.set(resume === 'idle' ? 'Preparing Form 50-162…' : 'Updating Form 50-162…');

      return this.submit.enroll(submission, address, ownerRelationship).pipe(
        tap(res => {
          this.savedKey = key;
          this.settle(res);
        }),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 409) {
            // Already submitted, or already signed: the details can't change
            this.error.set(UNAVAILABLE_ERROR);
            this.dispatch('UNAVAILABLE');
          } else {
            // (A 429 never gets here: it ends the visit at /too-many-requests; see signupThrottleInterceptor)
            this.error.set(REJECTED_ERROR);
            this.dispatch('SAVE_FAILED', resume);
          }
          return EMPTY;
        }),
      );
    });
  }

  private settle(res: CustomerIntakeResponse): void {
    if (res.signing) {
      this.request.set(res.signing);
      this.dispatch('OPENED');
    } else if (res.requiresLogin) {
      this.request.set(null);
      this.dispatch('LOGIN_REQUIRED');
    } else {
      this.request.set(null);
      this.error.set(UNAVAILABLE_ERROR);
      this.dispatch('UNAVAILABLE');
    }
  }

  /** Applies the event if the current state allows it; returns whether it did. */
  private dispatch(event: SignupEnrollmentEvent, resume?: SignupEnrollmentState): boolean {
    const next = nextEnrollmentState(this.state(), event, resume);
    if (next) this.state.set(next);
    return next !== null;
  }
}
